<template>
  <div>
    <RfPromoDetailsInfoRow title="Limits">
      <template #promodetail>
        <div>
          <div v-if="item.sequence_id">
            Inherited from <router-link :to="sequenceLink">{{ item.sequence_name }}</router-link>
          </div>
          <div v-else>
            <div v-if="is_limited || is_capped || is_budgeted || hasUserLimit || hasDeliveryLimit">
              <div v-if="is_limited">
                <span v-if="currLimit > 0"
                  >Impression limit:
                  <b>{{ currLimit }} {{ pluralize("impression", currLimit) }}</b> remaining.</span
                >
                <span v-else>Impression limit exceeded</span>
              </div>
              <div v-if="is_capped">
                Frequency cap: Show no more than
                <b>{{ capped_value }} {{ pluralize("time", capped_value) }}</b>
                <span v-if="cap_type === VISIT.value"> per <b>visit</b>. </span>
                <span v-else-if="cap_type === DAYS.value">
                  over <b>{{ capped_days }} {{ pluralize("day", capped_days) }}</b
                  >.
                </span>
              </div>
              <div v-if="is_budgeted">
                Budget limit: <b>{{ currBudget }} ({{ currBudgetPercent }}%)</b> remaining.
              </div>
              <div v-if="hasUserLimit">
                <span v-if="currUsers > 0">
                  User limit:
                  <b>{{ currUsers }} {{ pluralize("user", currUsers) }}</b> remaining.
                </span>
                <span v-else>User limit exceeded</span>
              </div>

              <div v-if="hasDeliveryLimit">
                <span v-if="currDeliveries > 0">
                  Delivery limit:
                  <b>{{ currDeliveries }} {{ pluralize("deliveries", currDeliveries) }}</b>
                  remaining.
                </span>
                <span v-else>Delivery limit exceeded</span>
              </div>
            </div>
            <div v-else>-</div>
            <div v-if="!item.sequence_id" class="pd-info-edit edit-freq-cap">
              <RfButton
                iconSize="16"
                title="edit"
                icon="md:edit"
                color="accent"
                size="x-small"
                variant="plain"
                density="compact"
                :disabled="editDisabled || !item?.filter"
                @click="showPromoDetailDialog"
              />
            </div>
          </div>
        </div>
      </template>
    </RfPromoDetailsInfoRow>
    <RfTraffic
      ref="traffic"
      :model="item"
      :title="`Limit this ${itemType} to:`"
      @confirm="submitConfiguration"
    />
  </div>
</template>

<script>
import RfTraffic from "@/components/RfPathCreate/RfTraffic.vue";
import RfPromoDetailsInfoRow from "@/components/RfPromotions/RfPromoDetailsInfoRow.vue";
import RfButton from "@/components/buttons/RfButton.vue";
import StringUtils from "@/utils/StringUtils";
import { useToastsStore } from "@/pinia/toastsStore";
import { DAYS, VISIT } from "@/utils/constants/FrequencyCapConstants";
import { useSequencesStore } from "@/pinia/sequencesStore";
import { usePromptStore } from "@/pinia/promptStore";

export default {
  name: "RfPromoLimitsInfo",
  components: { RfPromoDetailsInfoRow, RfTraffic, RfButton },
  props: ["item", "editDisabled"],
  setup: () => ({
    pluralize: StringUtils.pluralize,
    toastsStore: useToastsStore(),
    VISIT,
    DAYS,
    sequencesStore: useSequencesStore(),
    promptStore: usePromptStore(),
  }),
  computed: {
    is_capped() {
      return this.item.filter.frequency_cap.is_capped;
    },
    cap_type() {
      return this.item.filter.frequency_cap.cap_seconds === VISIT.value ? VISIT.value : DAYS.value;
    },
    capped_value() {
      return this.item.filter.frequency_cap.cap;
    },
    capped_days() {
      return Math.floor(this.item.filter.frequency_cap.cap_seconds / (3600 * 24));
    },
    is_budgeted() {
      return this.item.filter.supply.is_budgeted;
    },
    currBudget() {
      return this.item.filter.supply.budget - (this.item.curr_budget || 0);
    },
    currBudgetPercent() {
      const budget = parseInt(this.item.filter.supply.budget);
      const curr_budget = parseInt(this.item.curr_budget);
      if (budget <= 0) return 0;

      return 100 - parseInt((curr_budget / budget) * 100);
    },
    is_limited() {
      return this.item.filter.traffic.is_limited;
    },
    currLimit() {
      return this.item.filter.traffic.limit - (this.item.curr_limit || 0);
    },
    sequenceLink() {
      return { path: `/apps/${this.$route.params.aid}/experiences/${this.item.sequence_id}` };
    },
    hasUserLimit() {
      return this.item.filter.users.is_limited;
    },
    currUsers() {
      return this.item.filter.users.limit - (this.item.curr_users || 0);
    },
    hasDeliveryLimit() {
      return this.item.filter.deliveries.is_limited;
    },
    currDeliveries() {
      return this.item.filter.deliveries.limit - (this.item.curr_deliveries || 0);
    },
    itemType() {
      if (this.item.sequence_type) return "guide";
      return "prompt";
    },
  },
  methods: {
    showPromoDetailDialog() {
      this.$refs.traffic.open();
    },
    async submitConfiguration(newPath) {
      try {
        const appId = this.$route.params.aid;
        let title = "Prompt";
        if (this.item.subject_name === "Sequence") {
          title = "Guide";
          await this.sequencesStore.updateSequence({
            appId,
            sequenceId: this.$route.params.sid,
            modelSequence: newPath,
          });
        } else {
          await this.promptStore.updatePrompt({
            appId,
            pathId: this.$route.params.pid,
            modelPath: newPath,
          });
        }
        this.toastsStore.create({ type: "success", body: `${title} updated successfully` });
      } catch (e) {
        this.toastsStore.create({ type: "error", body: e.message });
      }
    },
  },
};
</script>
